import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  FlaskConical,
  Loader2,
  Upload,
  FileText,
  Sparkles,
  Copy,
  Download,
  History,
  Trash2,
  ShieldAlert,
  ShieldCheck,
  RefreshCw,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  runAdminResearch,
  listResearchHistory,
  getResearchHistoryItem,
  deleteResearchHistoryItem,
  listResearchVersions,
} from "@/lib/api/admin-research.functions";
import { exportResearchPdf, exportResearchDocx } from "@/lib/admin-research-export";

export const Route = createFileRoute("/_authenticated/portal/admin/research")({
  head: () => ({
    meta: [
      { title: "AI Research (Admin) | Nkadimeng Danny LCS" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminResearchPage,
});

const MAX_BYTES = 15 * 1024 * 1024;
const ACCEPTED = ".pdf,.txt,.md,.png,.jpg,.jpeg,.webp";

function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || "");
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

async function readAsText(file: File): Promise<string> {
  return await file.text();
}

const MODES = [
  { value: "memorandum", label: "Research Memorandum (IRAC)" },
  { value: "brief", label: "Executive Brief" },
  { value: "opinion", label: "Legal Opinion" },
  { value: "summary", label: "Document Summary" },
  { value: "extraction", label: "Data / Key Points Extraction" },
  { value: "article", label: "Legal Article (OSCOLA, publication-ready)" },
  {
    value: "short_legal_article",
    label: "Short Legal Article (1,000–1,500 words, Task 3 guide, OSCOLA)",
  },
  { value: "assignment", label: "Assignment Assistant (LLB, OSCOLA)" },
  { value: "case_summary", label: "Case Summary (9-component structure guide)" },
  { value: "case_note", label: "Case Note (academic, OSCOLA)" },
  { value: "heads_of_argument", label: "Heads of Argument (SA court format)" },
  { value: "letter_of_demand", label: "Letter of Demand" },
  { value: "contract_review", label: "Contract Review Report" },
  { value: "chronology", label: "Chronology of Events" },
  { value: "comparative_analysis", label: "Comparative Legal Analysis" },
  { value: "literature_review", label: "Literature Review (OSCOLA)" },
] as const;

type Mode = (typeof MODES)[number]["value"];
type Accuracy = "balanced" | "accurate";

type ResearchResult = {
  id?: string | null;
  root_id?: string | null;
  version?: number | null;
  output: string;
  model: string;
  mode: string;
  disclaimer: string;
  generated_at: string;
  originality_score?: number | null;
  flagged_passages?: { sentence: string; overlap: number }[];
  question: string;
  jurisdiction: string;
  article_title?: string;
  author_name?: string;
  topic_keywords?: string[];
  short_article_validation?: {
    valid: boolean;
    errors: { code: string; message: string; section?: string }[];
    warnings: { code: string; message: string; section?: string }[];
    totalWords: number;
    sectionWords?: Record<string, number>;
    bodySectionCount?: number;
  } | null;
};

function scoreColor(score: number | null | undefined): string {
  if (score == null) return "bg-slate-100 text-slate-700 border-slate-200";
  if (score >= 85) return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (score >= 65) return "bg-amber-100 text-amber-800 border-amber-200";
  return "bg-rose-100 text-rose-800 border-rose-200";
}

function AdminResearchPage() {
  const qc = useQueryClient();
  const runFn = useServerFn(runAdminResearch);
  const listFn = useServerFn(listResearchHistory);
  const getFn = useServerFn(getResearchHistoryItem);
  const delFn = useServerFn(deleteResearchHistoryItem);
  const versionsFn = useServerFn(listResearchVersions);

  const [question, setQuestion] = useState("");
  const [scenario, setScenario] = useState("");
  const [context, setContext] = useState("");
  const [jurisdiction, setJurisdiction] = useState("South Africa");
  const [mode, setMode] = useState<Mode>("memorandum");
  const [accuracy, setAccuracy] = useState<Accuracy>("balanced");
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<ResearchResult | null>(null);
  // Article metadata (used mainly in "article" mode; safe on any mode).
  const [articleTitle, setArticleTitle] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");
  // Versioning — regenerating a draft chains onto the current result.
  const [regenerateAsVersion, setRegenerateAsVersion] = useState(false);

  const parsedKeywords = useMemo(
    () =>
      keywordsInput
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 20),
    [keywordsInput],
  );

  const history = useQuery({
    queryKey: ["admin-research-history"],
    queryFn: () => listFn(),
  });

  const versions = useQuery({
    queryKey: ["admin-research-versions", result?.root_id ?? result?.id],
    queryFn: () =>
      versionsFn({ data: { root_id: (result?.root_id ?? result?.id) as string } }),
    enabled: !!(result?.root_id ?? result?.id),
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const documents: { filename: string; mime_type: string; base64: string }[] = [];
      let documentText = "";
      for (const f of files) {
        if (f.size > MAX_BYTES) throw new Error(`"${f.name}" is too large — max 15 MB.`);
        const base64 = await readAsBase64(f);
        documents.push({
          filename: f.name,
          mime_type: f.type || "application/octet-stream",
          base64,
        });
        if (
          (f.type && (f.type.startsWith("text/") || f.type === "application/json")) ||
          /\.(txt|md|csv|json)$/i.test(f.name)
        ) {
          try {
            documentText += (await readAsText(f)).slice(0, 200_000) + "\n\n";
          } catch {
            /* ignore */
          }
        }
      }
      const parentId =
        regenerateAsVersion && result?.id ? result.id : undefined;
      return runFn({
        data: {
          question,
          scenario,
          context,
          jurisdiction,
          mode,
          accuracy,
          documents,
          document_text: documentText.slice(0, 400_000),
          article_title: articleTitle,
          author_name: authorName,
          topic_keywords: parsedKeywords,
          parent_id: parentId,
        },
      });
    },
    retry: (count, err: any) =>
      count < 1 && /failed to fetch|network|load failed/i.test(String(err?.message ?? err)),
    onError: (err: any) => {
      const raw = String(err?.message ?? err);
      const description = /failed to fetch|network|load failed/i.test(raw)
        ? "The connection dropped before the AI finished — this usually means the request was too large or took too long. Try 'Balanced' accuracy, fewer/smaller attachments, or a shorter question."
        : raw;
      toast.error("Research failed", { description });
    },
    onSuccess: (data: any) => {
      setResult({ ...data, question, jurisdiction });
      setRegenerateAsVersion(false);
      qc.invalidateQueries({ queryKey: ["admin-research-history"] });
      qc.invalidateQueries({ queryKey: ["admin-research-versions"] });
      const structureErrors =
        (data?.case_summary_validation?.errors?.length ?? 0) +
        (data?.short_article_validation?.errors?.length ?? 0);
      if (structureErrors) {
        toast.warning("Output needs review", {
          description: `${structureErrors} guide-compliance issue(s) remain — see the validation panel below.`,
        });
      } else {
        toast.success("Research complete");
      }
    },

  });

  const flagged = useMemo(() => result?.flagged_passages ?? [], [result]);

  async function copyOutput() {
    if (!result?.output) return;
    await navigator.clipboard.writeText(result.output);
    toast.success("Copied to clipboard");
  }

  function slugish(s: string) {
    return (s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60);
  }

  function baseFilename(r: ResearchResult) {
    const stamp = new Date(r.generated_at).toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const titleSlug = r.article_title ? slugish(r.article_title) : "";
    const ver = r.version ? `-v${r.version}` : "";
    return titleSlug
      ? `${titleSlug}${ver}-${r.mode}-${stamp}`
      : `research-${r.mode}${ver}-${stamp}`;
  }

  function buildExportMeta(r: ResearchResult) {
    return {
      title:
        r.article_title?.trim() ||
        `Research ${r.mode.toUpperCase()}${r.version ? ` (v${r.version})` : ""}`,
      articleTitle: r.article_title,
      authorName: r.author_name,
      topicKeywords: r.topic_keywords,
      version: r.version ?? undefined,
      question: r.question,
      mode: r.mode,
      model: r.model,
      jurisdiction: r.jurisdiction,
      generatedAt: r.generated_at,
      originalityScore: r.originality_score ?? null,
    };
  }

  function downloadMd() {
    if (!result?.output) return;
    const headerLines: string[] = [];
    if (result.article_title) headerLines.push(`# ${result.article_title}`);
    if (result.author_name) headerLines.push(`_By ${result.author_name}_`);
    if (result.version) headerLines.push(`_Revision v${result.version}_`);
    if (result.topic_keywords?.length)
      headerLines.push(`_Keywords: ${result.topic_keywords.join(", ")}_`);
    const preface = headerLines.length ? headerLines.join("\n\n") + "\n\n---\n\n" : "";
    const blob = new Blob([preface + result.output], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.href = url;
    a.download = `${baseFilename(result)}.md`;
    window.document.body.appendChild(a);
    a.click();
    window.document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function downloadPdf() {
    if (!result) return;
    try {
      await exportResearchPdf(result.output, buildExportMeta(result), baseFilename(result));
    } catch (err: any) {
      toast.error("PDF export failed", { description: err?.message ?? String(err) });
    }
  }

  async function downloadDocx() {
    if (!result) return;
    try {
      await exportResearchDocx(result.output, buildExportMeta(result), baseFilename(result));
    } catch (err: any) {
      toast.error("DOCX export failed", { description: err?.message ?? String(err) });
    }
  }

  async function openHistory(id: string) {
    try {
      const res = (await getFn({ data: { id } })) as { item: any };
      const item = res.item;
      const r: ResearchResult = {
        id: item.id,
        root_id: item.root_id ?? item.id,
        version: item.version ?? 1,
        output: item.output,
        model: item.model,
        mode: item.mode,
        disclaimer:
          "AI-generated. Verify every authority, citation, quotation, and legal proposition against the primary source before relying on it.",
        generated_at: item.created_at,
        originality_score: item.originality_score,
        flagged_passages: (item.flagged_passages as any) ?? [],
        question: item.question,
        jurisdiction: item.jurisdiction,
        article_title: item.article_title ?? "",
        author_name: item.author_name ?? "",
        topic_keywords: (item.topic_keywords as any) ?? [],
      };
      setResult(r);
      setQuestion(item.question);
      setContext(item.context ?? "");
      setScenario(item.scenario ?? "");
      setJurisdiction(item.jurisdiction);
      setMode(item.mode as Mode);
      setAccuracy(item.accuracy as Accuracy);
      setArticleTitle(item.article_title ?? "");
      setAuthorName(item.author_name ?? "");
      setKeywordsInput(
        Array.isArray(item.topic_keywords) ? item.topic_keywords.join(", ") : "",
      );
      setFiles([]);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err: any) {
      toast.error("Could not load entry", { description: err?.message ?? String(err) });
    }
  }

  async function removeHistory(id: string) {
    if (!confirm("Delete this research entry permanently?")) return;
    try {
      await delFn({ data: { id } });
      qc.invalidateQueries({ queryKey: ["admin-research-history"] });
      if (result?.id === id) setResult(null);
      toast.success("Deleted");
    } catch (err: any) {
      toast.error("Delete failed", { description: err?.message ?? String(err) });
    }
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-md bg-navy text-navy-foreground">
          <FlaskConical className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl text-navy">AI Research & Document Analysis</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Admin-only research tool. Ask a question or upload a document (PDF, image, text). The AI
            produces structured, original output grounded in what you supply — with uncertain facts
            flagged rather than guessed. Every run is saved to history and scored for originality.
          </p>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr,320px]">
        {/* Main column */}
        <div className="space-y-6 min-w-0">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
            className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4"
          >
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="mode">Output type</Label>
                <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                  <SelectTrigger id="mode" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="accuracy">Model tier</Label>
                <Select value={accuracy} onValueChange={(v) => setAccuracy(v as Accuracy)}>
                  <SelectTrigger id="accuracy" className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balanced">
                      Balanced (Gemini 2.5 Flash — fast, low cost)
                    </SelectItem>
                    <SelectItem value="accurate">
                      High accuracy (Gemini 2.5 Pro — slower, richer)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {mode === "case_summary" && (
              <div className="rounded-lg border border-gold/40 bg-secondary/40 p-4 text-xs leading-relaxed text-muted-foreground">
                <p className="font-semibold text-navy">
                  Case summaries follow the Case Summary Structure Guide
                </p>
                <p className="mt-1.5">
                  Title: “[Case Name] v. [Case Name]: Case Summary”. Ten
                  sections in order: 1. Citation &amp; basic information ·
                  2. Introduction (100–150w) · 3. Facts, chronological (300–400w) ·
                  4. Legal issues framed as questions · 5. Arguments, petitioner
                  then respondent (250–350w) · 6. Court's reasoning (400–500w) ·
                  7. Judgment &amp; ratio decidendi (distinguished from obiter) ·
                  8. Critical analysis — significance, implications, evaluation
                  (300–400w) · 9. Conclusion (150–200w) · 10. References.
                </p>
                <p className="mt-1.5">
                  Target 1,500–2,000 words excluding citations, footnotes and
                  bibliography. OSCOLA is the only permitted citation style and
                  a consistency validator checks every footnote before the run
                  is saved. Quotes capped at two sentences with a paragraph
                  pinpoint. Plagiarism must stay below 15%. The DOCX export is
                  Times New Roman 12pt, 1.5 spacing, justified — and is
                  re-parsed after generation to confirm it.
                </p>

              </div>
            )}

            {mode === "short_legal_article" && (
              <div className="rounded-lg border border-gold/40 bg-secondary/40 p-4 text-xs leading-relaxed text-muted-foreground">
                <p className="font-semibold text-navy">
                  Short legal articles follow the Task 3 Short Legal Article guide
                </p>
                <p className="mt-1.5">
                  Title in the form “Main Title: Subtitle”. Five parts:
                  Introduction (150–200w — hook, context, explicit thesis,
                  roadmap) · two or three headed main-body sections (650–1,000w
                  combined — legal framework, case law or legislative analysis,
                  critical argument) · Conclusion (150–200w with a concrete
                  recommendation, no new authority) · References.
                </p>
                <p className="mt-1.5">
                  Total 1,000–1,500 words excluding footnotes and bibliography.
                  OSCOLA only — every case, statute, factual claim, secondary
                  argument and quotation carries a footnote with a pinpoint.
                  Third person, active voice, no contractions, 15–25 word
                  sentences, one idea per paragraph. Plagiarism below 15%; no
                  fabricated authority. A structure validator plus the OSCOLA
                  consistency validator run automatically and the draft is
                  re-generated until it complies. The DOCX export is Times New
                  Roman 12pt, 1.5 spacing, fully justified.
                </p>
              </div>
            )}



            <div>
              <Label htmlFor="question">Research question</Label>
              <Textarea
                id="question"
                required
                minLength={8}
                maxLength={2000}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="E.g. Analyse the enforceability of the restraint of trade clause in the attached employment contract under SA labour law."
                className="mt-1 min-h-[90px]"
              />
            </div>

            <div>
              <Label htmlFor="scenario">Scenario / facts (optional)</Label>
              <Textarea
                id="scenario"
                maxLength={20000}
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
                placeholder="Describe the client scenario or facts of the matter — parties, timeline, what happened, what the client wants to achieve."
                className="mt-1 min-h-[100px]"
              />
            </div>

            <div>
              <Label htmlFor="context">Additional context / notes (optional)</Label>
              <Textarea
                id="context"
                maxLength={20000}
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Paste relevant statutes, excerpts, or research notes. Text here is also used for the originality check."
                className="mt-1 min-h-[80px]"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="jurisdiction">Jurisdiction</Label>
                <Input
                  id="jurisdiction"
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  maxLength={120}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="doc-file">
                  Documents (optional — PDF, image, text; up to 8 files, max 15 MB each)
                </Label>
                <Input
                  id="doc-file"
                  type="file"
                  multiple
                  accept={ACCEPTED}
                  onChange={(e) =>
                    setFiles(e.target.files ? Array.from(e.target.files).slice(0, 8) : [])
                  }
                  className="mt-1"
                />
                {files.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center gap-1">
                        <FileText className="h-3 w-3" /> {f.name} ·{" "}
                        {(f.size / 1024).toFixed(1)} KB
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Similarity scoring compares the AI output against your scenario, context, and any
                  uploaded text/markdown files. PDFs are analysed by the AI but not yet included in
                  the local similarity check.
                </p>
              </div>
            </div>

            {/* Article metadata (optional; recommended for the Article mode) */}
            <div className="rounded-lg border border-dashed border-border bg-background/60 p-3 space-y-3">
              <p className="text-xs font-semibold text-navy">
                Article metadata{" "}
                <span className="font-normal text-muted-foreground">
                  (used in exports and page headers — recommended for Legal Article mode)
                </span>
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="article-title">Article title</Label>
                  <Input
                    id="article-title"
                    value={articleTitle}
                    onChange={(e) => setArticleTitle(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Restraint of Trade in South African Employment Contracts"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="author-name">Author name</Label>
                  <Input
                    id="author-name"
                    value={authorName}
                    onChange={(e) => setAuthorName(e.target.value)}
                    maxLength={120}
                    placeholder="e.g. Danny Nkadimeng"
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="keywords">Topic keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  value={keywordsInput}
                  onChange={(e) => setKeywordsInput(e.target.value)}
                  maxLength={400}
                  placeholder="e.g. restraint of trade, employment law, LRA, Basson v Chilwan"
                  className="mt-1"
                />
                {parsedKeywords.length > 0 && (
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {parsedKeywords.length} keyword{parsedKeywords.length === 1 ? "" : "s"}:{" "}
                    {parsedKeywords.join(" · ")}
                  </p>
                )}
              </div>
            </div>

            {result?.id && (
              <label className="flex items-start gap-2 rounded-md border border-border bg-background/50 p-2 text-xs text-navy">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={regenerateAsVersion}
                  onChange={(e) => setRegenerateAsVersion(e.target.checked)}
                />
                <span>
                  Save the next run as a new revision of the current draft
                  {result.version ? ` (currently v${result.version})` : ""}. Previous
                  revisions stay in history and can be restored from the Versions panel.
                </span>
              </label>
            )}

            <Button
              type="submit"
              disabled={mutation.isPending}
              className="bg-navy hover:bg-navy/90"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analysing…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />{" "}
                  {regenerateAsVersion && result?.id ? "Save new revision" : "Run research"}
                </>
              )}
            </Button>
          </form>

          {result?.output && (
            <section className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-display text-lg text-navy">
                    {result.article_title?.trim() || "Result"}
                    {result.version ? (
                      <span className="ml-2 rounded-full border border-navy/30 bg-secondary/40 px-2 py-0.5 text-[11px] font-medium text-navy">
                        v{result.version}
                      </span>
                    ) : null}
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    {result.model} · {result.mode} ·{" "}
                    {new Date(result.generated_at).toLocaleString("en-ZA")}
                    {result.author_name ? ` · ${result.author_name}` : ""}
                  </p>
                  {result.topic_keywords && result.topic_keywords.length > 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Keywords: {result.topic_keywords.join(", ")}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={copyOutput}>
                    <Copy className="mr-1 h-3 w-3" /> Copy
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={downloadMd}>
                    <Download className="mr-1 h-3 w-3" /> .md
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={downloadPdf}>
                    <Download className="mr-1 h-3 w-3" /> PDF
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={downloadDocx}>
                    <Download className="mr-1 h-3 w-3" /> DOCX
                  </Button>
                </div>
              </div>

              {/* Versions panel */}
              {(versions.data?.items?.length ?? 0) > 1 && (
                <div className="rounded-lg border border-border bg-background p-3">
                  <p className="mb-2 text-xs font-semibold text-navy">
                    Revisions ({versions.data?.items?.length ?? 0})
                  </p>
                  <ul className="space-y-1 text-xs">
                    {(versions.data?.items ?? []).map((v: any) => {
                      const isCurrent = v.id === result.id;
                      return (
                        <li
                          key={v.id}
                          className={`flex items-center justify-between gap-2 rounded border p-2 ${
                            isCurrent
                              ? "border-navy bg-secondary/40"
                              : "border-border bg-card"
                          }`}
                        >
                          <div className="min-w-0">
                            <span className="font-semibold text-navy">v{v.version}</span>
                            <span className="ml-2 text-muted-foreground">
                              {new Date(v.created_at).toLocaleString("en-ZA")}
                            </span>
                            {v.originality_score != null && (
                              <span className="ml-2 text-muted-foreground">
                                · {Number(v.originality_score).toFixed(0)}/100
                              </span>
                            )}
                          </div>
                          {isCurrent ? (
                            <span className="rounded bg-navy px-1.5 py-0.5 text-[10px] font-medium text-navy-foreground">
                              current
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openHistory(v.id)}
                              className="rounded border border-navy/30 px-2 py-0.5 text-[11px] text-navy hover:bg-secondary/50"
                            >
                              Restore
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}


              {/* Originality panel */}
              <div className="rounded-lg border border-border bg-background p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {(result.originality_score ?? 0) >= 85 ? (
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    ) : (
                      <ShieldAlert className="h-4 w-4 text-amber-600" />
                    )}
                    <span className="text-sm font-medium text-navy">
                      Originality score
                    </span>
                  </div>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${scoreColor(
                      result.originality_score,
                    )}`}
                  >
                    {result.originality_score != null
                      ? `${result.originality_score.toFixed(1)} / 100`
                      : "N/A"}
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Local 6-word shingle overlap against your context / text file. Higher is better.
                  Not a substitute for a full plagiarism scan against external corpora.
                </p>
                {flagged.length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer text-amber-800">
                      {flagged.length} passage{flagged.length === 1 ? "" : "s"} look close to your
                      source — review before sending
                    </summary>
                    <ul className="mt-2 space-y-1">
                      {flagged.map((p, i) => (
                        <li
                          key={i}
                          className="rounded border border-amber-200 bg-amber-50 p-2 text-amber-900"
                        >
                          <div className="text-[10px] font-semibold uppercase tracking-wide">
                            {(p.overlap * 100).toFixed(0)}% overlap
                          </div>
                          <p className="mt-0.5">{p.sentence}</p>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>

              {result.short_article_validation && (
                <div
                  className={`rounded-lg border p-4 text-xs leading-relaxed ${
                    result.short_article_validation.valid
                      ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                      : "border-amber-300 bg-amber-50 text-amber-900"
                  }`}
                >
                  <p className="font-semibold">
                    {result.short_article_validation.valid
                      ? "Task 3 compliance: PASS"
                      : "Task 3 compliance: review needed"}
                  </p>
                  <p className="mt-1">
                    {result.short_article_validation.totalWords} words (limit
                    1,000–1,500, excluding footnotes and bibliography) ·{" "}
                    {result.short_article_validation.bodySectionCount ?? 0} main-body
                    section(s)
                  </p>
                  {result.short_article_validation.errors.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-4">
                      {result.short_article_validation.errors.map((e, i) => (
                        <li key={i}>{e.message}</li>
                      ))}
                    </ul>
                  )}
                  {result.short_article_validation.warnings.length > 0 && (
                    <ul className="mt-2 list-disc space-y-1 pl-4 opacity-80">
                      {result.short_article_validation.warnings.map((w, i) => (
                        <li key={i}>{w.message}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}



              <article className="prose prose-sm max-w-none prose-headings:text-navy prose-headings:font-display">
                <ReactMarkdown>{result.output}</ReactMarkdown>
              </article>
              <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                <Upload className="mr-1 inline h-3 w-3" />
                {result.disclaimer}
              </p>
            </section>
          )}
        </div>

        {/* History sidebar */}
        <aside className="rounded-2xl border border-border bg-card p-4 shadow-soft h-fit lg:sticky lg:top-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <History className="h-4 w-4 text-navy" />
              <h3 className="font-display text-sm text-navy">Research history</h3>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => history.refetch()}
              disabled={history.isFetching}
            >
              <RefreshCw
                className={`h-3 w-3 ${history.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
          {history.status === "pending" ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : (history.data?.items?.length ?? 0) === 0 ? (
            <p className="text-xs text-muted-foreground">
              No saved runs yet. Every research you run will appear here.
            </p>
          ) : (
            <ul className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {(history.data?.items ?? []).map((it: any) => (
                <li
                  key={it.id}
                  className={`group rounded-lg border p-2 text-xs transition ${
                    result?.id === it.id
                      ? "border-navy bg-secondary/50"
                      : "border-border bg-background hover:border-navy/40"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => openHistory(it.id)}
                    className="block w-full text-left"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-navy">
                        {it.mode}
                      </span>
                      <span
                        className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${scoreColor(
                          it.originality_score,
                        )}`}
                      >
                        {it.originality_score != null
                          ? `${Number(it.originality_score).toFixed(0)}`
                          : "—"}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-navy">{it.question}</p>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">
                      {new Date(it.created_at).toLocaleString("en-ZA")}
                      {it.document_filename ? ` · ${it.document_filename}` : ""}
                    </p>
                  </button>
                  <div className="mt-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeHistory(it.id)}
                      className="text-[10px] text-rose-700 hover:underline inline-flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
